import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import styles from "./run-detail.module.css";

type RunStatus = "queued" | "running" | "completed" | "failed";
type TabId = "timeline" | "diagnostics" | "artefacts" | "logs";

type WorkflowMeta = {
  /** camelCase workflow id, same shape as `runs.tsx`/`runs-table.tsx`. */
  name: string;
  displayName: string;
  description: string;
  /** Pool of step names this workflow can break its run into — a run uses
   * the first 1-3 of these, picked deterministically per run id. */
  steps: string[];
};

const STATUS_LABEL: Record<RunStatus, string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_TONE: Record<RunStatus, StatusPillTone> = {
  queued: "neutral",
  running: "info",
  completed: "success",
  failed: "error",
};

const TABS: { id: TabId; label: string }[] = [
  { id: "timeline", label: "Timeline" },
  { id: "diagnostics", label: "Diagnostics" },
  { id: "artefacts", label: "Artefacts" },
  { id: "logs", label: "Logs" },
];

/**
 * Small pool of workflows this app already renders elsewhere (`runs.tsx`,
 * `runs-table.tsx`, `active.tsx`) — reused here (rather than invented) so a
 * run detail page always names a workflow that's consistent with the rest
 * of the mock dataset. `identifyCompanyUpdate`'s description matches the
 * live reference app's copy for that workflow verbatim (confirmed via
 * chrome-devtools against http://localhost:5183/admin/workflows/runs/:id);
 * the rest are written in the same voice for workflows that don't have a
 * reference detail page to copy from.
 */
const WORKFLOWS: WorkflowMeta[] = [
  {
    name: "ingestGmailInbox",
    displayName: "Ingest Gmail Inbox",
    description:
      "Poll a connected mailbox for new messages, thread them against existing deal records, and enqueue each new message for classification. Runs continuously per connected inbox.",
    steps: ["fetchMessages", "threadMatch", "enqueueClassification"],
  },
  {
    name: "classifyInboundEmail",
    displayName: "Classify Inbound Email",
    description:
      "Run a single ingested email through the inbound classifier to route it to the right downstream workflow (company update, scheduling, compliance, or none). Chains a follow-up run when a positive classification requires one.",
    steps: ["classify", "chainFollowUp"],
  },
  {
    name: "identifyCompanyUpdate",
    displayName: "Identify Company Update",
    description:
      "Classify one ingested email (a company_update_identification classification row): decide whether it is a metrics update, upload the LLM artefacts, and — when positive — chain a metrics extraction run. Replaces the company-update-identifier daemon; normally enqueued per row at email ingest.",
    steps: ["classify"],
  },
  {
    name: "syncAffinityCRM",
    displayName: "Sync Affinity CRM",
    description:
      "Pull the latest list, organisation, and interaction changes from Affinity and reconcile them against the local CRM mirror. Runs on a fixed schedule independent of any single deal.",
    steps: ["fetchChangeset", "reconcileRecords", "persist"],
  },
  {
    name: "refreshPortfolioMetrics",
    displayName: "Refresh Portfolio Metrics",
    description:
      "Recompute headline portfolio metrics (ARR, burn, runway) for every active investment from the latest extracted data-room figures. Triggered after a metrics extraction run completes.",
    steps: ["loadLatestFigures", "computeMetrics", "publish"],
  },
  {
    name: "enrichCompanyProfile",
    displayName: "Enrich Company Profile",
    description:
      "Augment a portfolio company's profile with the latest firmographic and funding data from third-party providers. Runs whenever a company record is created or its domain changes.",
    steps: ["fetchProviderData", "mergeProfile"],
  },
  {
    name: "scoreDealSentiment",
    displayName: "Score Deal Sentiment",
    description:
      "Score a deal's most recent correspondence and meeting notes for sentiment, then update the deal's momentum indicator on the pipeline board.",
    steps: ["gatherContext", "scoreSentiment", "updatePipeline"],
  },
  {
    name: "generateBoardPack",
    displayName: "Generate Board Pack",
    description:
      "Assemble a board pack PDF from the latest portfolio metrics, deal updates, and fund performance figures, then upload it as a run artefact for review.",
    steps: ["collectSections", "renderDocument", "uploadArtefact"],
  },
  {
    name: "generateWeeklyDigest",
    displayName: "Generate Weekly Digest",
    description:
      "Summarise the previous week's sentiment forms into an AI-generated digest and queue it for Monday delivery to the partner distribution list.",
    steps: ["aggregateForms", "generateSummary", "queueDelivery"],
  },
  {
    name: "notifyPartnerSlack",
    displayName: "Notify Partner Slack",
    description:
      "Post a formatted update to the configured partner Slack channel once its triggering event (a new deal, a completed run, a flagged risk) has been confirmed.",
    steps: ["formatMessage", "postToSlack"],
  },
  {
    name: "sendNotification",
    displayName: "Send Notification",
    description:
      "Deliver a queued notification to its recipient over the configured channel (email, Slack, or in-app) and record the delivery result.",
    steps: ["renderNotification", "deliver"],
  },
  {
    name: "syncCalendarEvents",
    displayName: "Sync Calendar Events",
    description:
      "Reconcile a connected calendar's upcoming events against scheduled meetings on the deal pipeline, creating or updating linked meeting records as needed.",
    steps: ["fetchEvents", "matchToDeals", "updateRecords"],
  },
];

/** Fixed display dates this mock dataset revolves around — the same
 * DD/MM/2026 values already used as literal strings in `runs.tsx`. */
const DATE_POOL = ["08/07/2026", "07/07/2026", "06/07/2026", "05/07/2026"];

// ---------------------------------------------------------------------------
// Deterministic derivation — turns an arbitrary `:runId` string into stable,
// internally-consistent mock content. No Math.random()/Date.now()/new Date():
// everything below is arithmetic on the id string itself, so the same runId
// always renders the exact same page.
// ---------------------------------------------------------------------------

/** FNV-1a-style string hash → a 32-bit seed. */
function hashSeed(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** mulberry32 — a small deterministic PRNG. Same seed → same infinite
 * sequence of [0, 1) draws every time, which is what makes every value
 * derived from it below reproducible for a given run id. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function hexDigits(draw: () => number, count: number): string {
  let out = "";
  for (let i = 0; i < count; i++) {
    out += Math.floor(draw() * 16).toString(16);
  }
  return out;
}

/** `HH:MM:SS`, wrapping seconds-of-day back into range. Deliberately not
 * date-aware (no day rollover) — a fixed simplification, fine for mock
 * timestamps that are always small offsets from the same day. */
function formatClock(totalSeconds: number): string {
  const wrapped = ((totalSeconds % 86400) + 86400) % 86400;
  const h = Math.floor(wrapped / 3600);
  const m = Math.floor((wrapped % 3600) / 60);
  const s = wrapped % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

/** `2s` / `2m 41s` — matches the duration formatting already used in
 * `runs.tsx`/`runs-table.tsx`. */
function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${pad2(seconds)}s`;
}

/** `822ms` / `2s` / `2m 41s` — same as `formatDuration` but sub-second
 * values (as seen on the reference timeline ruler) render as milliseconds. */
function formatMsDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return formatDuration(Math.round(ms / 1000));
}

type TimelineStep = {
  name: string;
  startMs: number;
  durationMs: number;
  durationLabel: string;
  depth: 0 | 1;
  visualStatus: "completed" | "running" | "failed";
};

type RunDetail = {
  workflow: WorkflowMeta;
  status: RunStatus;
  /** Short secondary identifier shown next to the workflow name in the
   * header card — a separate uuid-shaped id (distinct from the full run
   * id), matching the reference app's header layout. */
  secondaryId: string;
  deployment: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  durationLabel: string;
  /** `null` when the run is still queued and hasn't started — nothing to
   * draw a waterfall for yet. */
  timeline: TimelineStep[] | null;
  timelineTotalMs: number;
};

/**
 * Derives a full, internally-consistent mock run detail record purely from
 * the `:runId` string — there is no lookup table an arbitrary incoming id
 * could hit, since each Runs table on this route tree keeps its own local
 * mock array (see `../runs-table.tsx`/`./runs.tsx`/`./active.tsx`). Every
 * value pulled from the seeded PRNG below (workflow, status, ids,
 * timestamps, step breakdown) is drawn in a fixed order, so the same runId
 * always reproduces the same page.
 */
function deriveRunDetail(runId: string): RunDetail {
  const draw = mulberry32(hashSeed(runId));

  const workflow = WORKFLOWS[Math.floor(draw() * WORKFLOWS.length)];

  const statusRoll = Math.floor(draw() * 20);
  const status: RunStatus =
    statusRoll < 13 ? "completed" : statusRoll < 16 ? "running" : statusRoll < 18 ? "queued" : "failed";

  const secondaryId = `${hexDigits(draw, 8)}-${hexDigits(draw, 4)}-${hexDigits(draw, 4)}-${hexDigits(draw, 4)}-${hexDigits(draw, 12)}`;
  const deployment = hexDigits(draw, 7);

  const date = DATE_POOL[Math.floor(draw() * DATE_POOL.length)];
  const createdSeconds = Math.floor(draw() * 86400);
  const createdAt = `${date}, ${formatClock(createdSeconds)}`;

  const durationSeconds = 1 + Math.floor(draw() * 360);
  const startDelaySeconds = Math.floor(draw() * 5);
  const startedSeconds = createdSeconds + startDelaySeconds;
  const completedSeconds = startedSeconds + durationSeconds;

  const startedAt = status === "queued" ? null : `${date}, ${formatClock(startedSeconds)}`;
  const completedAt =
    status === "completed" || status === "failed"
      ? `${date}, ${formatClock(completedSeconds)}`
      : null;

  const durationLabel = status === "queued" ? "—" : formatDuration(durationSeconds);

  let timeline: TimelineStep[] | null = null;
  const timelineTotalMs = durationSeconds * 1000;

  if (status !== "queued") {
    const stepCount = 1 + Math.floor(draw() * Math.min(3, workflow.steps.length));
    const chosenSteps = workflow.steps.slice(0, stepCount);

    const leadInFrac = 0.08 + draw() * 0.12;
    const leadOutFrac = 0.04 + draw() * 0.08;
    const midFrac = 1 - leadInFrac - leadOutFrac;
    const midMs = midFrac * timelineTotalMs;

    const weights = chosenSteps.map(() => 0.5 + draw());
    const weightSum = weights.reduce((sum, w) => sum + w, 0);

    let cursorMs = leadInFrac * timelineTotalMs;
    const subSteps: TimelineStep[] = chosenSteps.map((name, index) => {
      const stepDurationMs = Math.max(Math.round((weights[index] / weightSum) * midMs), 1);
      const startMs = cursorMs;
      cursorMs += stepDurationMs;
      const isLast = index === chosenSteps.length - 1;
      const visualStatus: TimelineStep["visualStatus"] =
        isLast && status === "failed" ? "failed" : isLast && status === "running" ? "running" : "completed";
      return {
        name,
        startMs,
        durationMs: stepDurationMs,
        durationLabel: formatMsDuration(stepDurationMs),
        depth: 1,
        visualStatus,
      };
    });

    const topVisualStatus: TimelineStep["visualStatus"] =
      status === "failed" ? "failed" : status === "running" ? "running" : "completed";

    timeline = [
      {
        name: workflow.name,
        startMs: 0,
        durationMs: timelineTotalMs,
        durationLabel,
        depth: 0,
        visualStatus: topVisualStatus,
      },
      ...subSteps,
    ];
  }

  return {
    workflow,
    status,
    secondaryId,
    deployment,
    createdAt,
    startedAt,
    completedAt,
    durationLabel,
    timeline,
    timelineTotalMs,
  };
}

function truncateRunId(runId: string): string {
  return runId.length > 18 ? `${runId.slice(0, 18)}…` : runId;
}

function EmptyCard({ text }: { text: string }) {
  return (
    <section className={styles.emptyCard}>
      <p className={styles.emptyState}>{text}</p>
    </section>
  );
}

const STEP_ICON: Record<TimelineStep["visualStatus"], typeof CheckCircle2> = {
  completed: CheckCircle2,
  running: Loader2,
  failed: XCircle,
};

function RunTimeline({ steps, totalMs }: { steps: TimelineStep[]; totalMs: number }) {
  const safeTotalMs = totalMs > 0 ? totalMs : 1;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    fraction,
    label: formatMsDuration(Math.round(fraction * safeTotalMs)),
  }));

  return (
    <section className={styles.timelineCard}>
      <div className={cn(styles.gridRow, styles.rulerRow)}>
        <span className={styles.rulerStepLabel}>Step</span>
        <div className={styles.rulerTrack}>
          {ticks.map((tick) => (
            <span
              key={tick.fraction}
              className={styles.rulerTick}
              style={{
                left: `${tick.fraction * 100}%`,
                transform:
                  tick.fraction === 0
                    ? "none"
                    : tick.fraction === 1
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
              }}
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <div>
        {steps.map((step) => {
          const Icon = STEP_ICON[step.visualStatus];
          return (
            <div key={`${step.depth}-${step.name}`} className={cn(styles.gridRow, styles.stepRow)}>
              <div className={styles.rowLabel} style={{ paddingLeft: step.depth * 16 }}>
                <Icon
                  className={cn(
                    styles.rowIcon,
                    styles[`rowIcon_${step.visualStatus}`],
                    step.visualStatus === "running" && styles.spin,
                  )}
                  aria-hidden="true"
                />
                <span className={step.depth === 0 ? styles.rowNameTop : styles.rowNameSub}>
                  {step.name}
                </span>
                {step.depth > 0 && <span className={styles.rowDuration}>{step.durationLabel}</span>}
              </div>
              <div className={styles.rowTrack}>
                <div
                  className={cn(styles.bar, styles[`bar_${step.visualStatus}`], step.depth > 0 && styles.barSub)}
                  style={{
                    left: `${(step.startMs / safeTotalMs) * 100}%`,
                    width: `${Math.max((step.durationMs / safeTotalMs) * 100, 1)}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * `/workflows/runs/:runId` route: the drill-down detail page reached by
 * clicking a row in any of this app's Runs tables (`../runs-table.tsx`,
 * `./runs.tsx`, `./active.tsx`). Those tables each keep their own local
 * mock `WorkflowRun[]` array (not exported, not shared), so there is no
 * real lookup table for an arbitrary incoming `:runId` — instead every
 * piece of content on this page (workflow, status, ids, timestamps, the
 * step waterfall) is derived deterministically from the id string itself
 * via `deriveRunDetail`, so a given runId always renders identically no
 * matter which table linked to it. There is no live workflow-orchestration
 * backend in `platform` yet.
 */
export function WorkflowRunDetailPage() {
  const { runId = "" } = useParams<{ runId: string }>();
  const detail = useMemo(() => deriveRunDetail(runId), [runId]);
  const [activeTab, setActiveTab] = useState<TabId>("timeline");

  useDocumentTitle(`Run ${truncateRunId(runId)}`);

  // Published into the shell's secondary-header slot (see
  // `secondary-header-context.tsx`) instead of rendered inline — every leaf
  // page on this route tree puts its sub-navigation there, not in its own
  // page body. `.spacer` mirrors the structural-readiness convention already
  // used by `definitions.tsx`/`scheduled.tsx`/`infra.tsx`'s filter rows: no
  // header-level action button exists for this page yet, but the row is laid
  // out ready for one.
  const tabBarRow = useMemo(
    () => (
      <div className={styles.tabBarRow}>
        <div className={styles.tabBar} role="tablist" aria-label="Run detail views">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={cn(styles.tab, activeTab === tab.id && styles.tabActive)}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span className={styles.spacer} />
      </div>
    ),
    [activeTab],
  );

  useSecondaryHeader(tabBarRow);

  return (
    <div className={styles.page}>
      <section className={styles.headerCard}>
        <div className={styles.headerTop}>
          <h2 className={styles.name}>{detail.workflow.displayName}</h2>
          <span className={styles.secondaryId}>{detail.secondaryId}</span>
          <StatusPill tone={STATUS_TONE[detail.status]}>{STATUS_LABEL[detail.status]}</StatusPill>
        </div>
        <p className={styles.description}>{detail.workflow.description}</p>

        <div className={styles.metaGrid}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Run ID</span>
            <span className={cn(styles.metaValue, styles.mono)}>{runId}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Deployment</span>
            <span className={cn(styles.metaValue, styles.mono)}>{detail.deployment}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Duration</span>
            <span className={styles.metaValue}>{detail.durationLabel}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Created</span>
            <span className={styles.metaValue}>{detail.createdAt}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Started</span>
            <span className={styles.metaValue}>{detail.startedAt ?? "—"}</span>
          </div>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Completed</span>
            <span className={styles.metaValue}>{detail.completedAt ?? "—"}</span>
          </div>
        </div>
      </section>

      {activeTab === "timeline" &&
        (detail.timeline ? (
          <RunTimeline steps={detail.timeline} totalMs={detail.timelineTotalMs} />
        ) : (
          <EmptyCard text="This run is queued and hasn't started yet." />
        ))}
      {activeTab === "diagnostics" && <EmptyCard text="No diagnostics for this run." />}
      {activeTab === "artefacts" && <EmptyCard text="No artefacts for this run." />}
      {activeTab === "logs" && <EmptyCard text="No logs for this run." />}
    </div>
  );
}
